import { useMutation, useQueryClient } from '@tanstack/react-query';
import { handleErrors } from '../../../utils/errors';
import SharedDialog from '../../../shared/shared-dialog';
import { useStore } from '../../state/zustand-store';
import { useAnalytics } from '../../../shared/analytics/create-events';
import * as analytics from '../../../shared/analytics/analytics.json';
import { ModuleList } from '../../../shared/analytics/module-list';
import {
  BillingInformationModel,
  StripeCreditCardPaymentSourceModel,
  StripePaymentSourceModel,
} from '../../../api/billing-types';
import { BillingKeys, updateAutoPay } from '../../../api/billing-api';
import { useState } from 'react';
import ChangeLogDialog from '../../../shared/change-log-dialog';
import {
  BillingRoles,
  isAbleToSeeUIElement,
} from '../../../utils/roles/client-roles';
import { Controller, useForm } from 'react-hook-form';
import Visa from '../../../assets/cards/visa.png';
import Amex from '../../../assets/cards/amex.png';
import Mastercard from '../../../assets/cards/mastercard.png';
import Discover from '../../../assets/cards/discover.png';
import { HubSpotCompany } from '../../../api/billing-types';
import { Messages } from '../../../utils/message-constants';

import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Checkbox from '@mui/material/Checkbox';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormLabel from '@mui/material/FormLabel';
import Link from '@mui/material/Link';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import AccountBalance from '@mui/icons-material/AccountBalance';

interface UpdateAutoPayDialogProps {
  open: boolean;
  handleClose: () => void;
  billingInfoModel: BillingInformationModel;
  changeLogRoles: string[];
  hubSpotCompany: HubSpotCompany;
}

export default function UpdateAutoPayDialog({
  open,
  handleClose,
  billingInfoModel,
  changeLogRoles,
  hubSpotCompany,
}: UpdateAutoPayDialogProps) {
  const { createEvent } = useAnalytics();
  const { setSnackbar } = useStore();
  const queryClient = useQueryClient();
  const [openChangeLogDialog, setOpenChangeLogDialog] = useState(false);
  const [changeLogMessage, setChangeLogMessage] = useState('');
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [showMoreTerms, setShowMoreTerms] = useState(false);

  const creditCardPaymentsEnabled =
    hubSpotCompany.properties.credit_card_payments_enabled;

  const images = {
    Visa: Visa,
    'American Express': Amex,
    MasterCard: Mastercard,
    Discover: Discover,
  };

  const { mutate, isPending } = useMutation({
    mutationFn: (data: {
      isAutoPayEnabled: boolean;
      selectedMethod: string | undefined;
    }) =>
      updateAutoPay(
        data.isAutoPayEnabled,
        billingInfoModel.stripeSourceModels.find(
          (method) => method.metadata?.isAutoPaySource
        ),
        billingInfoModel.stripeSourceModels.find(
          (method) => method.id === data.selectedMethod
        ),
        changeLogMessage
      ),
    onSuccess: () => {
      setSnackbar({
        open: true,
        severity: 'success',
        message: Messages.Success.Updated('Autopay'),
      });

      queryClient.invalidateQueries({
        queryKey: [BillingKeys.CustomerBillingInformation],
      });

      setChangeLogMessage('');
      setOpenChangeLogDialog(false);
      handleClose();
    },
    onError: (error) => {
      handleErrors({
        error,
        snackbarMessage: Messages.Errors.Network.Updating('autopay'),
      });
    },
    onSettled: () => {
      createEvent(analytics.billing.payments.update_autopay, {
        module: ModuleList.modules.billing.name,
        section: ModuleList.modules.billing.sections.payments,
      });
    },
  });

  const {
    control,
    handleSubmit,
    watch,
    getValues,
    formState: { isSubmitting, isDirty },
  } = useForm({
    defaultValues: {
      isAutoPayEnabled:
        billingInfoModel.stripeSourceModels.some(
          (method) => method.metadata?.isAutoPaySource
        ) || false,
      selectedMethod:
        billingInfoModel.stripeSourceModels.find(
          (method) => method.metadata?.isAutoPaySource
        )?.id || billingInfoModel.stripeSourceModels[0].id,
    },
  });

  const proceedWithSubmit = (data: {
    isAutoPayEnabled: boolean;
    selectedMethod: string | undefined;
  }) => {
    if (
      isAbleToSeeUIElement(
        changeLogRoles,
        BillingRoles.canChangeAutoPaySource.value
      )
    ) {
      setOpenChangeLogDialog(true);
    } else {
      mutate(data);
    }
  };

  const isAutoPayEnabled = watch('isAutoPayEnabled');
  const selectedMethod = watch('selectedMethod');
  const selectedMethodType =
    billingInfoModel.stripeSourceModels.find(
      (method) => method.id === selectedMethod
    )?.stripePaymentSourceType ?? 'CreditCard';

  const oldAutopayMethod = billingInfoModel.stripeSourceModels.find(
    (method) => method.metadata?.isAutoPaySource
  );
  const oldAutopayMethodIsAch =
    oldAutopayMethod?.stripePaymentSourceType === 'Ach';
  const isSwitchingFromAutopayMethod = selectedMethod !== oldAutopayMethod?.id;
  const isLosingAutopay = !isAutoPayEnabled || isSwitchingFromAutopayMethod;
  const showBankAccountRemovalInfo = oldAutopayMethodIsAch && isLosingAutopay;

  function isCreditCard(
    paymentMethod: StripePaymentSourceModel
  ): paymentMethod is StripeCreditCardPaymentSourceModel {
    return paymentMethod.stripePaymentSourceType === 'CreditCard';
  }

  return (
    <SharedDialog
      open={open}
      handleClose={handleClose}
      maxWidth="md"
      title="Update Autopay"
      dialogContent={
        <Box>
          <Stack gap={2}>
            <Controller
              name={'isAutoPayEnabled'}
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  label="Autopay enabled"
                  control={
                    <Switch
                      onChange={() => field.onChange(!field.value)}
                      checked={field.value}
                    />
                  }
                  sx={{
                    width: 'fit-content',
                  }}
                />
              )}
            />

            <Controller
              name={'selectedMethod'}
              control={control}
              render={({ field }) => (
                <FormControl sx={{ pl: 4 }} disabled={!isAutoPayEnabled}>
                  <FormLabel>Select the payment method to update:</FormLabel>
                  <RadioGroup
                    value={field.value}
                    onChange={(e) => {
                      field.onChange(e.target.value);

                      const newMethod =
                        billingInfoModel.stripeSourceModels.find(
                          (method) => method.id === e.target.value
                        );

                      if (
                        newMethod?.stripePaymentSourceType !==
                        selectedMethodType
                      ) {
                        setAgreeToTerms(false);
                      }
                    }}
                    sx={{
                      display: 'flex',
                      gap: 1,
                    }}
                  >
                    {billingInfoModel.stripeSourceModels.map((method) => {
                      const isCreditCardMethod = isCreditCard(method);
                      const achNeedsVerification = method.showWarning;
                      const creditCardPaymentsDisabled =
                        !creditCardPaymentsEnabled && isCreditCardMethod;

                      return (
                        <Tooltip
                          title={
                            creditCardPaymentsDisabled
                              ? 'Credit card payments are disabled'
                              : achNeedsVerification
                              ? 'Needs verification'
                              : ''
                          }
                          arrow
                          key={method.id}
                        >
                          <FormControlLabel
                            value={method.id}
                            control={
                              <Radio
                                disabled={
                                  achNeedsVerification ||
                                  creditCardPaymentsDisabled ||
                                  !isAutoPayEnabled
                                }
                              />
                            }
                            label={
                              <Stack
                                flexDirection="row"
                                gap={1}
                                alignItems="center"
                              >
                                <Avatar
                                  src={
                                    images[method.brand as keyof typeof images]
                                  }
                                  variant="rounded"
                                  sx={{
                                    '&.Mui-disabled': {
                                      filter: 'grayscale(100%)',
                                      opacity: 0.5,
                                    },
                                  }}
                                  className={
                                    !isAutoPayEnabled ||
                                    achNeedsVerification ||
                                    creditCardPaymentsDisabled
                                      ? 'Mui-disabled'
                                      : ''
                                  }
                                >
                                  <AccountBalance />
                                </Avatar>
                                {isCreditCardMethod ? (
                                  <Typography>{`${method.brand} - ${method.last4}`}</Typography>
                                ) : (
                                  <Typography>
                                    {method.bank_name} - {method.last4}
                                  </Typography>
                                )}
                                {method.showWarning && (
                                  <Typography variant="caption" sx={{ ml: 2 }}>
                                    (Verification required to use for autopay)
                                  </Typography>
                                )}
                              </Stack>
                            }
                          />
                        </Tooltip>
                      );
                    })}
                  </RadioGroup>
                </FormControl>
              )}
            />
          </Stack>

          {isAutoPayEnabled && selectedMethodType === 'CreditCard' && (
            <Stack gap={1} sx={{ mt: 2 }}>
              <Typography variant="caption">
                By clicking update, you agree to Promevo's{' '}
                <Link
                  href="http://www.promevo.com/policies/promevoprivacypolicy.html"
                  target="_blank"
                >
                  privacy policy
                </Link>{' '}
                and you authorize Promevo to initiate recurring automatic
                charges to your Credit Card indicated below, in order to make
                payments on your order from Promevo, and, if necessary, to
                initiate transactions to correct any erroneous payment. Your
                AutoPay payments will begin on the Start Date listed upon
                completion of your Credit Card registration and will occur on
                the same date each month thereafter until your account is
                thereafter until your account is paid in full, terminated by
                Company (as provided below), or you revoke this authorization
                (as provided below). If any payment date falls on a weekend or
                holiday, the payment will be executed on the next business day.
                If the outstanding balance you owe on your Promevo contract is
                less than the payment amount stated above, you understand and
                agree that the final payment will be an amount equal to your
                total outstanding balance.{' '}
              </Typography>
              {showMoreTerms && (
                <Typography variant="caption">
                  If your Bank is unable to process any Credit Card entry, you
                  authorize Promevo to resubmit the Credit Card entry within the
                  next 3 to 5 business days, If your payment is dishonored, you
                  agree that Promevo may charge a late charge (if and to the
                  extent applicable), to the extent allowed by law. This
                  authorization will remain in full force and effect until
                  Promevo has received written notification from you (or either
                  one of you) of termination at the address above or you have
                  provided verbal notification by calling us at (888) 380-1061.
                  You agree to notify Promevo of any changes in your Credit Card
                  Account information or termination of this authorization at
                  least 15 days prior to the next billing date to afford a
                  reasonable opportunity for Promevo and your credit card
                  provider time to act. Promevo may, at any time, terminate your
                  right to make recurring credit card payments. Termination of
                  this authorization by either you or us does not affect your
                  Promevo account.
                </Typography>
              )}

              <Button
                onClick={() => setShowMoreTerms(!showMoreTerms)}
                size="small"
                sx={{
                  textDecoration: 'underline',
                  textTransform: 'none',
                  width: 'fit-content',
                  alignSelf: 'flex-end',
                }}
              >
                {showMoreTerms ? 'show less' : 'show more'}
              </Button>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreeToTerms}
                    onChange={() => setAgreeToTerms(!agreeToTerms)}
                    size="small"
                  />
                }
                label="I have read and agree to the terms and conditions above."
              />
            </Stack>
          )}

          {isAutoPayEnabled && selectedMethodType === 'Ach' && (
            <Stack gap={1} sx={{ mt: 2 }}>
              <Typography variant="caption">
                By clicking update, you agree to Promevo's{' '}
                <Link
                  href="http://www.promevo.com/policies/promevoprivacypolicy.html"
                  target="_blank"
                >
                  privacy policy
                </Link>{' '}
                and you authorize Promevo to initiate recurring electronic
                automated clearing house (ACH) debit entries from your Checking
                Account indicated above, at the Bank named above, in order to
                make payments on your order from Promevo, and, if necessary, to
                initiate transactions to correct any erroneous payment debit.
                Your AutoPay payments will begin on the Start Date listed above
                and will occur on the same date each month thereafter until your
                account is paid in full, terminated by Company (as provided
                below), or you revoke this authorization (as provided below). If
                any payment date falls on a weekend or holiday, the payment will
                be executed on the next business day. If the outstanding balance
                you owe on your Promevo contract is less than the payment amount
                stated above, you understand and agree that the final payment
                will be an amount equal to your total outstanding balance.
              </Typography>
              {showMoreTerms && (
                <Typography variant="caption">
                  If your Bank is unable to process and electronic ACH debit
                  entry, you authorize Promevo to resubmit the ACH debit entry
                  within the next 3 to 5 business days, and/or to submit a paper
                  draft to your Bank for processing and payment. If your payment
                  (whether in the form of an electronic ACH debit or a paper
                  draft) is dishonored or returned unpaid by your Bank, you
                  agree that Promevo may charge a return item fee and/or a late
                  charge (if and to the extend applicable), to the extent
                  allowed by law. You also acknowledge that your Bank amy also
                  impose its own additional fees according to your Deposit
                  Account Agreement with such bank. You acknowledge that the
                  origination of ACH debit entries to your Checking Account must
                  comply with and will be governed by the provisions of
                  applicable laws and rules of the National Automated Clearing
                  house applicable to the transaction. This authorization will
                  remain in full force and effect until Promevo has received
                  written notification from you (or either one of you) of
                  termination at the address above or you have provided verbal
                  notification by calling us at (888) 380-1061. You agree to
                  notify Promevo of any changes in your Checking Account
                  Information or terminiation of this authorization at least 15
                  days prior to the next billing date to afford a reasonable
                  opportunity for Promevo and your bank time to act. Promevo,
                  may, at any time, terminate your right to make recurring
                  electronic clearing house (ACH) debit entries. Termination of
                  this authorization by either you or us does not affect your
                  Promevo account.
                </Typography>
              )}

              <Button
                onClick={() => setShowMoreTerms(!showMoreTerms)}
                size="small"
                sx={{
                  textDecoration: 'underline',
                  textTransform: 'none',
                  width: 'fit-content',
                  alignSelf: 'flex-end',
                }}
              >
                {showMoreTerms ? 'show less' : 'show more'}
              </Button>

              <FormControlLabel
                control={
                  <Checkbox
                    checked={agreeToTerms}
                    onChange={() => setAgreeToTerms(!agreeToTerms)}
                    size="small"
                  />
                }
                label="I have read and agree to the terms and conditions above."
              />
            </Stack>
          )}

          <ChangeLogDialog
            open={openChangeLogDialog}
            handleClose={() => setOpenChangeLogDialog(false)}
            changeLogMessage={changeLogMessage}
            setChangeLogMessage={setChangeLogMessage}
            mutate={() => mutate(getValues())}
            mutateLoading={isPending}
          />
          {showBankAccountRemovalInfo && (
            <Alert severity="info" sx={{ alignItems: 'start', mt: 2 }}>
              Disabling auto pay or switching to a different payment method will
              delete the currently enabled bank account payment method. This
              payment method will need to be re-added for future use.
            </Alert>
          )}
        </Box>
      }
      primaryButton={{
        loading: isPending || isSubmitting,
        action: handleSubmit(proceedWithSubmit),
        disabled:
          isPending ||
          isSubmitting ||
          !isDirty ||
          (isAutoPayEnabled && !agreeToTerms),
        title: 'Update AutoPay',
      }}
    />
  );
}
